import { Component, OnInit, Input, ChangeDetectionStrategy } from '@angular/core';
import { PopoverController, ModalController, AlertController, LoadingController } from '@ionic/angular/lazy';
import { APIService } from 'src/app/services/api.service';
import { AuthService } from 'src/app/services/auth.service';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { ObserverService } from 'src/app/services/observer.service';
import { PermissionService } from 'src/app/services/permission.service';
import { PersonShippingAddressEditPage } from 'src/app/modals/person-shipping-address-edit/person-shipping-address-edit.page';
import { OrganisationShippingAddressEditPage } from 'src/app/modals/organisation-shipping-address-edit/organisation-shipping-address-edit.page';

@Component({
    selector: 'app-address-book-shipping-address-options',
    templateUrl: './address-book-shipping-address-options.component.html',
    styleUrls: ['./address-book-shipping-address-options.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class AddressBookShippingAddressOptionsComponent implements OnInit {

    @Input() address_picker_modal = null;
    @Input() address = null;
    @Input() organisation_id = null;
    @Input() person_id = null;
    spinner = null;
    modal_css_class = '';

    constructor(
        private popoverCtrl: PopoverController,
        public modalController: ModalController,
        public alertController: AlertController,
        public loadingController: LoadingController,
        private router: Router,
        private apiService: APIService,
        private authService: AuthService,
        private observerService: ObserverService,
        public permissionService: PermissionService
    ) { }

    ngOnInit() {

        if(Capacitor.isNativePlatform()) {
            this.modal_css_class = 'fullscreen';
        }


    }

    async dismiss() {
        try {
            await this.popoverCtrl.dismiss();
        } catch (e) {
            // click more than one time popover throws error, so ignore...
        }
    }

    edit() {
        if(this.address.entity_type == "person") {
            this.presentEditPersonShippingAddressModal().then(() => {
                this.dismiss();
            });
        }
        else if(this.address.entity_type == "organisation") {
            this.presentEditOrganisationShippingAddressModal().then(() => {
                this.dismiss();
            });
        }
    }

    deleteOrganisationShippingAddress() {
        return new Promise<void>((resolve, reject) => {
            this.presentSpinner("Deleting").then(() => {
                this.authService.isLoggedIn().then(() => {
                    this.apiService.deleteOrganisationShippingAddress(this.organisation_id, this.address.shipping_address_id).then(() => {
                        this.observerService.publish("organisation_shipping_address_deleted", this.address.shipping_address_id);
                        this.spinner.dismiss();
                        resolve();
                    })
                    .catch((error) => {
                        if(error.status == 400) {
                            this.presentAlert("Unknown error", "Unfortunately something went wrong when trying to delete an organisation shipping address.");
                        }
                        else if(error.status == 403) {
                            this.presentAlert("Insufficient permissions", "You don't have permission to delete an organisation shipping address.");
                        }
                        else if(error.status == 404) {
                            this.presentAlert("Unavailable", "Unfortunately we're unable to delete an organisation shipping address at this time.");
                        }
                        else if(error.status == 0) {
                            this.presentAlert("Connection failure", "Unable to reach the server. Please try again.");
                        }
                        this.spinner.dismiss();
                        reject();
                    });
                })
                .catch(() => {
                    this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                        window.location.reload();
                    });
                });
            });
        });
    }

    deletePersonShippingAddress() {
        return new Promise<void>((resolve, reject) => {
            this.presentSpinner("Deleting").then(() => {
                this.authService.isLoggedIn().then(() => {
                    this.apiService.deletePersonShippingAddress(this.person_id, this.address.shipping_address_id).then(() => {
                        this.observerService.publish("person_shipping_address_deleted", this.address.shipping_address_id);
                        this.spinner.dismiss();
                        resolve();
                    })
                    .catch((error) => {
                        if(error.status == 400) {
                            this.presentAlert("Unknown error", "Unfortunately something went wrong when trying to delete a person shipping address.");
                        }
                        else if(error.status == 403) {
                            this.presentAlert("Insufficient permissions", "You don't have permission to delete a person shipping address.");
                        }
                        else if(error.status == 404) {
                            this.presentAlert("Unavailable", "Unfortunately we're unable to delete a person shipping address at this time.");
                        }
                        else if(error.status == 0) {
                            this.presentAlert("Connection failure", "Unable to reach the server. Please try again.");
                        }
                        this.spinner.dismiss();
                        reject();
                    });
                })
                .catch(() => {
                    this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                        window.location.reload();
                    });
                });
            });
        });
    }

    async presentDeleteAlert() {
        const alert = await this.alertController.create({
            header: "Are you sure?",
            message: "Deleting a shipping address is permanent and it can't be restored.",
            cssClass:"alert",
            buttons: [
                {
                    text: 'Cancel',
                    role: 'cancel'
                },
                {
                    text: 'OK',
                    handler: () => {
                        if(this.address.entity_type == "person") {
                            this.deletePersonShippingAddress().then(() => {
                                this.dismiss();
                            });
                        }
                        else if(this.address.entity_type == "organisation") {
                            this.deleteOrganisationShippingAddress().then(() => {
                                this.dismiss();
                            });
                        }
                    }
                }
            ],
        });
        await alert.present();
    }

    async presentEditPersonShippingAddressModal() {
        const modal = await this.modalController.create({
            component: PersonShippingAddressEditPage,
            cssClass: this.modal_css_class,
            componentProps: {
                person_id: this.person_id,
                shipping_address_id: this.address.shipping_address_id
            }
        });
        return await modal.present();
    }

    async presentEditOrganisationShippingAddressModal() {
        const modal = await this.modalController.create({
            component: OrganisationShippingAddressEditPage,
            cssClass: this.modal_css_class,
            componentProps: {
                organisation_id: this.organisation_id,
                shipping_address_id: this.address.shipping_address_id
            }
        });
        return await modal.present();
    }

    async presentAlert(header, message) {
        const alert = await this.alertController.create({
            header: header,
            message: message,
            buttons: ["OK"],
        });
        await alert.present();
    }

    async presentSpinner(message) {
        this.spinner = await this.loadingController.create({
            message: message,
            showBackdrop: false,
        });
        await this.spinner.present();
    }

}
